import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardComp, ButtonComp } from "../../../components";
import { formatMoney } from "../../../utils";
import toast from "react-hot-toast";
import {
  useVerifyPaymentStatusPublicQuery,
  useManuallyVerifyPaymentPublicMutation,
} from "../../../store/member/member-contribution";
import { investorButtonProps } from "../../../components/Investor/investorButtonStyles";

/**
 * Public Paystack return page for marketplace pay_online orders.
 * Verifies by payment reference only (no auth required).
 */
export default function MarketplacePaymentConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState("processing");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const hasManuallyVerified = useRef(false);
  const retryTimeoutRef = useRef(null);

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 4000;

  const urlReference = searchParams.get("reference") || searchParams.get("trxref");
  const storedReference = sessionStorage.getItem("pending_marketplace_payment_reference");
  const paymentReference = urlReference || storedReference;
  const storedOrderNumber = sessionStorage.getItem("pending_marketplace_order_number");

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (storedReference && !urlReference) {
      navigate(
        `/invest/marketplace/payment/confirmation?reference=${encodeURIComponent(storedReference)}`,
        { replace: true }
      );
    }
  }, [storedReference, urlReference, navigate]);

  useEffect(() => {
    if (urlReference && storedReference) {
      sessionStorage.removeItem("pending_marketplace_payment_reference");
    }
  }, [urlReference, storedReference]);

  const {
    data: paymentData,
    error: paymentError,
    isLoading: isVerifyingPayment,
    refetch: refetchPayment,
  } = useVerifyPaymentStatusPublicQuery(
    { reference: paymentReference },
    { skip: !paymentReference, pollingInterval: 0 }
  );

  const [manuallyVerifyPayment, { isLoading: isManuallyVerifying }] =
    useManuallyVerifyPaymentPublicMutation();

  const markSuccess = useCallback((data) => {
    setPaymentStatus("success");
    setPaymentDetails(data);
    sessionStorage.removeItem("pending_marketplace_payment_reference");
  }, []);

  const markFailed = useCallback((message) => {
    setPaymentStatus("failed");
    setPaymentDetails({ message });
  }, []);

  useEffect(() => {
    if (!paymentReference) {
      markFailed("No payment reference found. If you completed payment, check your marketplace orders.");
      return;
    }

    if (isVerifyingPayment) return;

    if (paymentError) {
      if (retryCount < MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount((c) => c + 1);
          refetchPayment();
        }, RETRY_DELAY);
        return;
      }
      markFailed(paymentError?.data?.message || "Unable to verify payment status.");
      return;
    }

    const status = String(paymentData?.status || "").toUpperCase();
    if (status === "SUCCESSFUL" || status === "SUCCESS") {
      markSuccess(paymentData);
      return;
    }

    if (status === "FAILED" || status === "CANCELLED") {
      markFailed("Payment was not successful. Reserved stock may be released if the payment window expires.");
      return;
    }

    if (!hasManuallyVerified.current && retryCount === 0) {
      hasManuallyVerified.current = true;
      manuallyVerifyPayment({ reference: paymentReference })
        .unwrap()
        .then((data) => {
          const s = String(data?.status || "").toUpperCase();
          if (s === "SUCCESSFUL" || s === "SUCCESS") {
            markSuccess(data);
          } else {
            setRetryCount(1);
            retryTimeoutRef.current = setTimeout(() => refetchPayment(), RETRY_DELAY);
          }
        })
        .catch(() => {
          setRetryCount(1);
          retryTimeoutRef.current = setTimeout(() => refetchPayment(), RETRY_DELAY);
        });
      return;
    }

    if (retryCount < MAX_RETRIES) {
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount((c) => c + 1);
        refetchPayment();
      }, RETRY_DELAY);
      return;
    }

    markFailed(
      "Payment is still processing. Check your orders shortly, or contact support if it does not update."
    );
  }, [
    paymentReference,
    paymentData,
    paymentError,
    isVerifyingPayment,
    retryCount,
    refetchPayment,
    manuallyVerifyPayment,
    markSuccess,
    markFailed,
  ]);

  const orderNumber =
    paymentDetails?.order_number ||
    sessionStorage.getItem("pending_marketplace_order_number") ||
    storedOrderNumber ||
    null;

  const goToOrder = () => {
    sessionStorage.removeItem("pending_marketplace_order_number");
    if (orderNumber) {
      navigate(`/invest/marketplace/orders/${encodeURIComponent(orderNumber)}`);
      return;
    }
    navigate("/invest/requests?filter=marketplace");
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center px-3"
      style={{
        background: "linear-gradient(160deg, #f7f7f7 0%, #eaf1f4 45%, #ffffff 100%)",
      }}
    >
      <CardComp
        cardClassName="border-0 shadow-sm rounded-4 p-4 p-md-5"
        style={{ maxWidth: 480, width: "100%" }}
      >
        {paymentStatus === "processing" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#383838" }}>
              Confirming your order…
            </h4>
            <p className="text-muted small mb-0">
              We are verifying your payment
              {isManuallyVerifying ? " with Paystack" : ""}. This usually takes a few seconds.
            </p>
            <div className="text-center py-4">
              <div className="spinner-border" style={{ color: "var(--color-primary)" }} role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          </>
        )}

        {paymentStatus === "success" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#19bc31" }}>
              Payment successful
            </h4>
            <p className="text-muted small mb-3">
              Your payment was received and your order is confirmed.
            </p>
            {orderNumber ? (
              <p className="mb-1 small">
                <span className="text-muted">Order:</span> <strong>{orderNumber}</strong>
              </p>
            ) : null}
            {paymentDetails?.total_amount != null ? (
              <p className="mb-1 small">
                <span className="text-muted">Amount paid:</span>{" "}
                <strong>{formatMoney(paymentDetails.total_amount)}</strong>
              </p>
            ) : null}
            {paymentDetails?.payment_reference ? (
              <p className="mb-4 small text-muted">Ref: {paymentDetails.payment_reference}</p>
            ) : (
              <div className="mb-4" />
            )}
            <div className="d-flex flex-column gap-2">
              <ButtonComp
                btnText="View order"
                onClick={goToOrder}
                {...investorButtonProps.primary}
                ButtonInlineStyle={{
                  ...investorButtonProps.primary.ButtonInlineStyle,
                  width: "100%",
                }}
              />
              <ButtonComp
                btnText="Back to marketplace"
                onClick={() => {
                  sessionStorage.removeItem("pending_marketplace_order_number");
                  navigate("/invest/marketplace");
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  width: "100%",
                }}
              />
            </div>
          </>
        )}

        {paymentStatus === "failed" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#ff4343" }}>
              Payment not confirmed
            </h4>
            <p className="text-muted small mb-4">
              {paymentDetails?.message || "We could not confirm this payment."}
            </p>
            <div className="d-flex flex-column gap-2">
              <ButtonComp
                btnText="Try verify again"
                onClick={() => {
                  hasManuallyVerified.current = false;
                  setRetryCount(0);
                  setPaymentStatus("processing");
                  refetchPayment();
                }}
                loading={isManuallyVerifying}
                {...investorButtonProps.primary}
                ButtonInlineStyle={{
                  ...investorButtonProps.primary.ButtonInlineStyle,
                  width: "100%",
                }}
              />
              <ButtonComp
                btnText="View orders"
                onClick={() => {
                  toast("You can cancel an unpaid order from your order detail.");
                  goToOrder();
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  width: "100%",
                }}
              />
            </div>
          </>
        )}
      </CardComp>
    </div>
  );
}
